import { useState, useEffect } from 'react';
import { BsBriefcaseFill } from 'react-icons/bs';
import { FaHome } from 'react-icons/fa';
import { FiMapPin } from 'react-icons/fi';
import { combineLatest } from 'rxjs';
import Cookie from 'universal-cookie';

import { createAddressUser } from '../../api/wave/users';
import GuestUserStore from '../../store/guestUser';
import { Order } from '../../store/order';
import User from '../../store/user';
import { getAddressTypeInfo } from '../../utils/addressUtilities';
import { addToAnalytics } from '../../utils/analytics';
import AddressSelectionModal from '../AddressSelectionModal/AddressSelectionModal';
import AddressViewModal from '../AddressViewModal/AddressViewModal';
import { SecondaryButton } from '../Buttons/Buttons';
import GuestUserModal from '../GuestDetailsModal/GuestDetailsModal';

import styles from './AddressPicker.module.scss';

export default function AddressPicker({ guestTakeAway = false }) {
  const [address, setAddress] = useState(undefined);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [openAddressSelectionModal, setOpenAddressSelectionModal] = useState(false);
  const [guestUser, setGuestUser] = useState({});
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [user, setUser] = useState({});
  const cookies = new Cookie();
  const token = cookies.get('token');

  useEffect(() => {
    const user$ = combineLatest([User._lazyInit(), GuestUserStore._lazyInit()]).subscribe(res => {
      const _user = res[0];
      const guest = res[1];
      setUser(_user);
      setGuestUser(guest);
      const userAddress = _user?.mainAddress || _user?.address?.[0] || guest;
      setAddress(userAddress);
      Order.setSelectedAddress(userAddress);
    });

    return () => {
      user$.unsubscribe();
    };
  }, []);

  async function addAddress(newAddress) {
    addToAnalytics('addAddres', undefined, [['option', 'submit']]);
    const addressToCreate = { address: [{ ...newAddress }] };
    const response = await createAddressUser(token, addressToCreate);
    if (typeof response === 'string') {
      setOpenAddressModal(false);
      return;
    }
    User.addAddress(response?.address);
    const _user = User.getValue();
    if (!_user.mainAddress) User.updateMainAddress(response?.address);

    setOpenAddressModal(false);
  }

  function getAddressTypeIcon(type) {
    switch (type) {
      case 'home':
        return <FaHome className={styles.addressIcon} alt={type} />;
      case 'work':
        return <BsBriefcaseFill className={styles.addressIcon} alt={type} />;
      default:
        return <FiMapPin className={styles.addressIcon} alt={type} />;
    }
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.addressTitle}>{guestTakeAway ? <span>&#8203;</span> : 'ΔΙΕΥΘΥΝΣΗ ΠΑΡΑΔΟΣΗΣ'}</h2>
      {address ? (
        <>
          <p className={styles.instructions}>
            {guestTakeAway && 'Τα επιλεγμένα στοιχεία επικοινωνίας και η διεύθυνση σου.'}
            {user?._id && 'Η επιλεγμένη διεύθυνση στην οποία θα παραδώσουμε τις αγορές σου.'}
            {guestUser?.email &&
              !guestTakeAway &&
              'Τα επιλεγμένα στοιχεία επικοινωνίας και η διεύθυνση στην οποία θα παραδώσουμε τις αγορές σου.'}
          </p>
          <div className={styles.containerAddress}>
            <div className={styles.addressInfo}>
              {getAddressTypeIcon(address?.type)}
              <div className={styles.addressDetails}>
                {!guestUser?.email && (
                  <span className={styles.addressType}>{getAddressTypeInfo(address.type).label}</span>
                )}
                <span>{`${address.street} ${address.streetNo}, ${address.city}, ${address.postalCode}`}</span>
                {address.floor && address.nameAtBell && (
                  <span>{`όροφος: ${address.floor}, ${address.nameAtBell}`}</span>
                )}
                {address.contactPhone && (
                  <div>
                    {address.contactPhone}
                    {guestUser?.email && <span>, {guestUser.email}</span>}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.btnContainer}>
              {guestUser?.email ? (
                <SecondaryButton onClick={() => setIsGuestModalOpen(!isGuestModalOpen)}>
                  <p className={styles.buttonText}>Αλλαγή Στοιχείων</p>
                </SecondaryButton>
              ) : (
                <SecondaryButton onClick={() => setOpenAddressSelectionModal(!openAddressSelectionModal)}>
                  <p className={styles.buttonText}>Αλλαγή Διεύθυνσης</p>
                </SecondaryButton>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <p className={styles.instructions}>
            Δεν έχεις επιλεγμένη διεύθυνση, πάτα το κουμπί για να προσθέσεις μια στον λογαριασμό σου.
          </p>
          <div className={styles.btnContainer}>
            <SecondaryButton onClick={() => setOpenAddressModal(true)}>
              <p className={styles.buttonText}>Προσθήκη Διεύθυνσης</p>
            </SecondaryButton>
          </div>
        </>
      )}
      {openAddressSelectionModal && (
        <AddressSelectionModal isOpen={openAddressSelectionModal} setIsOpen={setOpenAddressSelectionModal} />
      )}
      {isGuestModalOpen && <GuestUserModal isOpen={isGuestModalOpen} setIsOpen={setIsGuestModalOpen} />}
      {openAddressModal && (
        <AddressViewModal isOpen={openAddressModal} setIsOpen={setOpenAddressModal} onSubmitCB={addAddress} />
      )}
    </section>
  );
}
